import React from "react";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Premium Skeleton Shimmer Component
 */
const Skeleton = ({
  width = "full",
  height = "4",
  variant = "rectangle", // 'rectangle', 'circle', 'text'
  className = "",
  animate = true,
}) => {
  const { isDark } = useTheme();

  // Tailwind can't see dynamically-built class names like `w-[${width}]` or
  // `h-${height}` at build time, so all sizing goes through inline styles.
  // Numeric strings are treated as Tailwind spacing steps (n × 4px).
  const toCss = (value) => {
    if (value == null || value === "full") return "100%";
    if (/^\d+$/.test(String(value))) return `${Number(value) * 4}px`;
    return value; // e.g. "48px", "60%", "3rem"
  };

  const baseClasses = `
    ${variant === "circle" ? "rounded-full" : "rounded-lg"}
    ${isDark ? "bg-zinc-800/50" : "bg-gray-200/60"}
    ${animate ? "animate-pulse" : ""}
    ${className}
  `;

  const style = { width: toCss(width), height: toCss(height) };

  return (
    <div
      className={baseClasses}
      style={style}
      aria-hidden="true"
    />
  );
};

export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  const { colors } = useTheme();
  return (
    <div className="w-full space-y-4 p-6">
      <div className="flex gap-4 mb-6">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height="6" width={`${100 / columns}%`} className="opacity-40" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`flex items-center gap-4 py-4 border-b ${colors.tableBorder} last:border-0`}>
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton key={j} height="4" width={`${100 / columns}%`} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton = () => (
    <div className="p-6 rounded-2xl border border-gray-200 dark:border-zinc-800 space-y-4">
        <Skeleton variant="circle" width="48px" height="12" />
        <Skeleton width="60%" height="6" />
        <Skeleton width="100%" height="24" />
    </div>
);

export const CalendarSkeleton = ({ view = "weekly" }) => {
  const { colors, isDark } = useTheme();
  const dayCount = view === "monthly" ? 7 : (view === "three_day" ? 3 : (view === "daily" ? 1 : 7));
  
  return (
    <div className={`w-full h-[70dvh] min-h-[420px] md:h-[600px] flex flex-col rounded-2xl border ${colors.tableBorder} overflow-hidden shadow-2xl`}>
      {/* Header */}
      <div className={`flex border-b ${colors.tableBorder} ${colors.tableHeader}`}>
        <div className="w-10 md:w-16 h-12 border-r border-gray-100 dark:border-zinc-800" />
        {Array.from({ length: dayCount }).map((_, i) => (
          <div key={i} className="flex-1 h-12 p-3 flex flex-col items-center justify-center gap-1 border-r last:border-0 border-gray-100 dark:border-zinc-800">
            <Skeleton width="30px" height="3" />
            <Skeleton width="20px" height="4" />
          </div>
        ))}
      </div>
      
      {/* Grid */}
      <div className="flex-1 overflow-hidden flex">
        <div className="w-10 md:w-16 flex flex-col border-r border-gray-100 dark:border-zinc-800">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-16 border-b border-gray-100 dark:border-zinc-800 p-2 flex justify-center">
              <Skeleton width="30px" height="3" className="opacity-40" />
            </div>
          ))}
        </div>
        <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${dayCount}, 1fr)` }}>
          {Array.from({ length: dayCount * 12 }).map((_, i) => (
             <div key={i} className="border-r border-b border-gray-50 dark:border-zinc-900/50 p-2">
                {i % 7 === 0 && <Skeleton width="80%" height="10" className="opacity-20 mt-2" />}
                {i % 13 === 0 && <Skeleton width="90%" height="14" className="opacity-10 mt-1" />}
             </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
